import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { getShopByOwnerStrict } from '../../../services/shop-service';
import { getSessionId } from '../../../services/api-client';
import { useShopTier } from '../../../hooks/useShopTier';
import { useAnalyticsSummary } from '../../../hooks/useAnalyticsSummary';
import type { WindowDays } from '../../../services/analytics-service';
import FoundingBadge from './FoundingBadge';
import PaywallCard from './PaywallCard';
import RetentionHero from './RetentionHero';
import BragChart from './BragChart';
import RegularsList from './RegularsList';
import AtRiskList from './AtRiskList';
import TopCustomers from './TopCustomers';
import RoiStrip from './RoiStrip';
import PeakHourChart from './PeakHourChart';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';
import ShopGoal from '../../dashboard/ShopGoal';
import { fetchShopGoal } from '../../../services/analytics-service';

export default function ShopAnalyticsDashboard() {
  const { userAddress } = useAuthStore();
  const [shopId, setShopId] = useState<string | null>(null);
  const [shopName, setShopName] = useState<string | null>(null);
  const [shopLookup, setShopLookup] = useState<'loading' | 'done' | 'error'>('loading');
  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  // Same card the customers see on HOME, from the same endpoint — so the
  // owner is never looking at a different number than their regulars are.
  const [goal, setGoal] = useState<{ goal: any; served: number } | null>(null);

  // Resolve owner's shop id. A failed lookup must NOT render as "no shop".
  useEffect(() => {
    if (!userAddress) return;
    setShopLookup('loading');
    getShopByOwnerStrict(userAddress)
      .then(s => {
        setShopId(s?.id || null);
        setShopName(s?.name || null);
        setShopLookup('done');
      })
      .catch(() => setShopLookup('error'));
  }, [userAddress]);

  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    fetchShopGoal(shopId)
      .then(r => { if (!cancelled) setGoal(r); })
      .catch(() => { /* the goal card is additive; never break the page */ });
    return () => { cancelled = true; };
  }, [shopId]);

  const { data: tierInfo, isLoading: tierLoading } = useShopTier(shopId);
  const isPaid = tierInfo?.tier === 'founding' || tierInfo?.tier === 'pro';
  const { data: summary, isLoading: summaryLoading, error } = useAnalyticsSummary(
    isPaid ? shopId : null,
    windowDays,
  );

  // Early-return branches below follow the same OwnerLoadingShell pattern
  // T12/T13 already applied to the settings pages and ShopOwnerDashboard —
  // this page never got that fix, so it was still a bare full-viewport dark
  // div with zero navigation on these branches.
  if (!userAddress) {
    return (
      <OwnerLoadingShell active="stats" statusLabel="STATS" title="Sign in">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <p className="font-heading text-[10px] text-body-grey">SIGN IN TO VIEW ANALYTICS</p>
        </div>
      </OwnerLoadingShell>
    );
  }

  if (shopLookup === 'loading' || tierLoading || (isPaid && summaryLoading)) {
    return (
      <OwnerLoadingShell active="stats" statusLabel="STATS" title="Loading…">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <p className="font-heading text-[10px] text-body-grey animate-pulse">LOADING ANALYTICS…</p>
        </div>
      </OwnerLoadingShell>
    );
  }

  if (shopLookup === 'error') {
    return (
      <OwnerLoadingShell active="stats" statusLabel="STATS" title="Connection glitch">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <h2 className="font-grotesk font-bold text-lg text-primary mb-2">Connection glitch</h2>
          <p className="font-grotesk text-sm text-body-grey mb-4">Couldn't load your shop — not a registration problem.</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
          >
            Retry
          </button>
        </div>
      </OwnerLoadingShell>
    );
  }

  if (!shopId) {
    return (
      <OwnerLoadingShell active="stats" statusLabel="STATS" title="No shop yet">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <p className="font-heading text-[10px] text-body-grey mb-4">NO SHOP REGISTERED</p>
          <a
            href="/shops/register"
            className="inline-block rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
          >
            Register a shop →
          </a>
        </div>
      </OwnerLoadingShell>
    );
  }

  return (
    <OwnerShell
      active="stats"
      header={
        <CoralHeader
          statusLabel="STATS"
          eyebrow={isPaid ? `LAST ${windowDays} DAYS` : 'FREE PLAN'}
          title={shopName || 'Analytics'}
        />
      }
    >
      <div className="max-w-5xl mx-auto pb-8">
        {tierInfo?.tier === 'founding' && (
          <div className="mb-3.5">
            <FoundingBadge number={tierInfo.foundingNumber} />
          </div>
        )}

        {!isPaid && <PaywallCard />}

        {isPaid && summary && (
          <div className="flex flex-col gap-3.5">
            {goal?.goal && <ShopGoal goal={goal.goal} served={goal.served} />}
            <RetentionHero rate={summary.retention.rate} cohortSize={summary.retention.cohortSize} />
            <BragChart
              total={summary.stamps.total}
              deltaPct={summary.stamps.delta_pct}
              daily={summary.stamps.daily}
              windowDays={windowDays}
              onWindowChange={setWindowDays}
            />
            <RoiStrip
              stamps={summary.roi.stamps}
              redemptions={summary.roi.redemptions}
              compsSent={summary.roi.comps_sent}
              compsRedeemed={summary.roi.comps_redeemed}
              estRevenue={summary.roi.est_revenue}
              compReturnRate={summary.roi.comp_return_rate}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <RegularsList regulars={summary.regulars} />
              <AtRiskList
                atRisk={summary.at_risk}
                tier={summary.shop.tier}
                shopId={shopId || undefined}
                authToken={getSessionId()}
              />
            </div>
            <TopCustomers customers={summary.top_customers || []} shopId={shopId!} />
            <PeakHourChart peakHours={summary.peak_hours} />
          </div>
        )}

        {isPaid && error && (
          <div className="bg-white rounded-[10px] p-6 text-center">
            <p className="font-grotesk font-bold text-error">Failed to load analytics</p>
            <p className="font-grotesk text-sm text-body-grey mt-2">{(error as Error).message}</p>
          </div>
        )}
      </div>
    </OwnerShell>
  );
}
